// The review screen's data seams (`client-rounds-review`): what a run IS to the screen,
// and how a run's trace becomes something scrubbable. Kept in `net/` for the same reason
// `replay-http.ts` holds the `OpenReplayClient` contract — this is the shape the
// eventual `rounds-http` run-list/trace-fetch surface (tasks group 8) will satisfy, and
// the screen must never construct a client of its own.
//
// Note what is deliberately ABSENT: there is no "submit"/"select candidate" method
// anywhere on these seams. Choosing a candidate for a graded round is a successor
// change's (`rounds-tournament`/`rounds-sandbox`); this slice's rail only shows runs and
// switches which one is under review, and the seam is narrow enough that it *cannot*
// do more.

import type {
  CoarseSample,
  DecodedTrace,
  DetailRecord,
  EvictedSpan,
  InFlightPrefix,
  TraceComponentName,
} from './rounds-trace';

/** The only run kind this slice produces: the player's colony **alone** on the full
 * field (**G3**), simulated against a seed the player can see. A graded round is
 * contested (**G4**) on a fresh, hidden seed (**G6**) — which is exactly why the review
 * screen frames a run's score as evidence, never as a forecast (**R3**). */
export type ReviewRunKind = 'sandbox';

/** The supporting stats shown beside the **G1** score — evidence about how the colony's
 * scripts behaved. */
export interface ReviewRunStats {
  /**
   * The highest living population observed over the run, or `null` when it was not recorded.
   *
   * Nullable because `rounds-http`'s run list does not carry a peak (implementation-review
   * **F1**): the honest options were "say it is not recorded" or "show the *final* population
   * under a peak label", and a number that reads as evidence while being about something else is
   * exactly the kind of quiet lie the score readout's whole framing (**R3**) exists to avoid.
   */
  peakPopulation: number | null;
  /** Nectar in the colony's stores at the end of the run. */
  nectarStored: number;
  /** The tick the colony was eliminated / starved out, or `null` if it survived. */
  eliminationTick: number | null;
}

/** One run produced this round, as the rail and the score readout see it. */
export interface ReviewRun {
  runId: string;
  /**
   * The run's **numbered identity** within the round ("Run 3") — the order it was
   * produced in, 1-based. Emphatically not a rank: the rail is ordered by when a run
   * happened, never by how it scored (**R3**).
   */
  number: number;
  kind: ReviewRunKind;
  seed: number;
  colonyCount: number;
  /** The trace's terminal tick — the run's duration, once divided by `tickRateHz`. */
  terminalTick: number;
  tickRateHz: number;
  /** The **G1** metric: living bees at the colony's end (queen counted, brood excluded). */
  score: number;
  stats: ReviewRunStats;
}

/**
 * A corruption discovered in a trace component — the same `(component, message)` pair
 * {@link TraceLoad}'s `corrupt` arm carries, reusable for a corruption that surfaces
 * **after** a successful open (implementation-review **F9**).
 */
export interface TraceCorruptionReport {
  component: TraceComponentName;
  message: string;
}

/**
 * A loaded trace as the review screen consumes it. Deliberately narrower than
 * `DecodedTrace`: it adds the residency question the tier indicator answers, because
 * the two-tier split (**A4**) is the screen's business and the byte format is not.
 */
export interface ReviewTrace {
  firstTick: number;
  terminalTick: number;
  tickRateHz: number;
  /** The match's field edge length, from the trace manifest's quantization
   * denominators — what the screen dequantizes positions against so a frame lands in
   * the right place. */
  fieldSize: number;
  /**
   * The run's **per-cell store capacity** (WAG-100): the multiplier that turns a colony's live
   * cell count at the playhead into its stored-nectar ceiling (`live cells × per-cell capacity`,
   * SIM-RULES §6). The cells are already in every detail record; this is the one balance figure
   * the client cannot derive from a trace's bytes.
   *
   * **Optional, unlike `tickRateHz`** — and the difference is the point rather than an omission.
   * A surface with no rate has to invent one or mislabel its clock, so that field is required and
   * has no default anywhere. A surface with no capacity has an honest rendering available: state
   * what is stored and claim no ceiling at all. There is therefore no wrong answer to substitute
   * here, and requiring it would only force every test double to restate a number it has no
   * opinion about (the `stateNames` precedent). Both shipped producers supply it.
   */
  perCellCapacity?: number;
  /** The coarse sample at-or-before `tick`. The coarse tier is downloaded whole, so
   * this answers for any tick in the trace's span — which is what makes coarse a real
   * view to fall back to rather than a placeholder. */
  coarseAt(tick: number): CoarseSample | null;
  /** The detail record for `tick`, or `null` when the range covering it is **not
   * resident** — in flight. Never a stall: the screen renders coarse instead. */
  detailAt(tick: number): DetailRecord | null;
  /**
   * The trace's run-global **state-name table**, which every detail bee's `stateId` indexes
   * (format version 6+, WAG-36). Match-static, so it is a value rather than a per-tick lookup.
   *
   * Optional so a test double that has no opinion about labels need not restate it; both shipped
   * producers supply it. Empty is a legitimate answer — a bot-only run interns nothing.
   */
  stateNames?: readonly string[];
  /** Requests residency of the range covering `tick`. Idempotent, fire-and-forget. A
   * request whose window falls inside an evicted span is suppressed rather than issued
   * and retried — waiting cannot help, and a request that can never be satisfied would
   * either retry forever or surface as a failure (`rounds-trace-retention`). */
  requestDetail(tick: number): void;
  /**
   * The trace's detail identity's evicted tick ranges — **normalized**: ascending,
   * non-overlapping and clamped to `[firstTick, terminalTick]` — the one canonical signal
   * the tier indicator and the scrub track both read, so they cannot disagree
   * (architecture-review **F3**). Empty when nothing has been evicted, which is every
   * trace before `rounds-trace-http` and every trace opened locally from a downloaded
   * file (`reviewTraceFromDecoded`'s default).
   */
  evictedSpans: readonly EvictedSpan[];
  /**
   * **How much of this trace exists yet**, as a monotone counter — present only for a trace that can
   * still grow, which is a run being **watched while it is written** and nothing else.
   *
   * It exists because identity is the wrong signal for that one trace. Every other `ReviewTrace` is
   * immutable, so "a different object" and "different content" are the same statement and a consumer
   * can depend on the reference. A watched run's extent arrives in deltas, and the accumulator used to
   * express each one as a brand-new trace — which meant the screen's draw effect, which names `trace`
   * in its dependencies, pushed a **full renderer sync per delta** whether or not the drawn frame had
   * moved. That is where in-flight watching's 108-155 ms frames came from, against a published
   * replay's 45 ms worst case over the same run.
   *
   * So the watcher now grows one trace in place and increments this instead. A consumer that must
   * react to growth names this value; one that only cares *which* trace it is holding keeps naming
   * the reference. Both signals stay honest, which they were not while identity carried both jobs.
   *
   * `undefined` for every published trace: there is nothing for it to count, and an absent field is a
   * stronger statement than a constant one — a consumer cannot mistake "never changes" for "has not
   * changed yet".
   */
  extentVersion?: number;
  /**
   * **The highest tick this trace actually holds a record for** — present only for a trace that is
   * still being written, absent for a published one, which holds all of them.
   *
   * Distinct from `terminalTick`, which for a watched trace is the run's *configured horizon* (what
   * the track depicts), and distinct again from the run's `ticksObserved` progress count (what the
   * writer has put on disk). Those two can be thousands of ticks ahead of this one: a device run
   * stops handing world state across the thread boundary after `DEFAULT_WATCH_WINDOW_TICKS`, so
   * beyond this tick there is nothing to draw at all — measured as the field emptying 3.8 s into a
   * run at 16× while the progress readout kept climbing.
   *
   * This is therefore the bound the playhead and the seek rule are clamped to (`watchedHeadTick` on
   * `WrittenExtent`); `ticksObserved` is left to the progress bar alone. `null` before any record has
   * arrived — an extent of zero width, which is a normal state and not an absence.
   *
   * **Read it, never cache it.** The watched trace grows in place, so implementations expose this as
   * a live getter; a caller that snapshots the number will be clamping to a bound that stopped being
   * true one delta later.
   */
  writeHeadTick?: number | null;
  /**
   * Observes a corruption discovered **after** this trace opened — a detail range that came
   * back as `rounds-trace`'s typed corruption (`trace_corrupt`/`trace_absent`) or as bytes
   * that will not decode. Returns an unsubscribe; a listener registered after the fact is
   * still told immediately, so a subscription can never miss the one report.
   *
   * This exists because of the **D4**, **D6** fail-closed rule (implementation-review
   * **F9**). Detail arrives by range *after* open, and "the range is not resident yet" and
   * "the range is damaged" render identically — as coarse — unless the damaged case is
   * observable. Coarse is a legitimate way to watch a match, so a corrupt trace posing as
   * coarse is indistinguishable from a healthy wide-zoom view; the screen must be able to
   * stop rendering the trace and name the component instead. A *transient* transport failure
   * is deliberately NOT reported here: it keeps showing coarse and stays retryable.
   *
   * Optional, because a trace decoded from whole, checksum-verified bytes (`openTrace` →
   * {@link reviewTraceFromDecoded}) has no post-open failure mode to report: for it, every
   * corruption is already caught at open.
   */
  onCorrupt?(listener: (report: TraceCorruptionReport) => void): () => void;
}

/** Opening a run's trace either yields something scrubbable, or reports `rounds-trace`'s
 * corruption error for a component the manifest declares. There is no third outcome in
 * this slice: eviction moved to `rounds-trace-http` (artifacts-review **F6**), so absent
 * declared bytes are never intentional here and must never be dressed up as coarse. */
export type TraceLoad =
  | { kind: 'loaded'; trace: ReviewTrace }
  | { kind: 'corrupt'; component: TraceComponentName; message: string }
  /**
   * The trace is intact but was written at a format version this build no longer reads — **stale,
   * not damaged** (`bee-state-flash` task 4.5/4.6).
   *
   * The third outcome the note above says this slice does not have, added deliberately when
   * device-retained traces made it reachable: a run written before a format bump is not absent,
   * evicted or damaged, and folding it into `corrupt` would report a routine app update as data
   * loss. Only the device-local store produces it; a served trace is always at the serving build's
   * own version.
   */
  | { kind: 'unsupported'; formatVersion: number; supportedVersion: number };

/**
 * Where a trace comes from (`rounds-client` **D3**). The two arms are deliberately
 * asymmetric, not two spellings of the same address:
 *
 *   * `sandboxRun` — the caller's own run, opened by the numeric id `ColonyRunController`
 *     already carries, inside the caller's own sandbox scope. The credential that
 *     authorizes it is the session token, and only the caller's own bytes are ever
 *     reachable through it.
 *   * `gradedRound` — one colony's view of a graded round's **single shared trace**,
 *     opened by tournament and round under the tournament-scoped credential. It carries
 *     **no `runId` field at all** — not a `null`, not an omitted one filtered server-side,
 *     but a shape that cannot express one — because the graded read is authorized by a
 *     credential that deliberately does not accept a sandbox run id as a key into another
 *     player's bytes (`client-rounds-review`, "no run id anywhere in the request").
 *
 * The shape itself is what states which credential and which route family a request must
 * go through; nothing downstream needs to ask.
 */
export type TraceLocator =
  | { kind: 'sandboxRun'; runId: number }
  | { kind: 'gradedRound'; round: number; colony: number }
  /**
   * The tournament's shared **preview** trace (`rounds-preview-run`) — nobody's run, resolved
   * server-side from the tournament the caller's bearer belongs to. Structurally incapable of
   * naming a run id: there is no field here to carry one, matching the server route, which
   * accepts none either.
   */
  | { kind: 'tournamentPreview' };

/** The trace-open seam. Production will satisfy this over `rounds-http`'s coarse-whole +
 * detail-by-range routes; tests inject a reader over the committed fixture. Widened from a
 * bare `runId: string` to a {@link TraceLocator} (**D3**) so a graded read is expressible on
 * the same seam the sandbox screen already opens its own runs through. */
export interface TraceReader {
  open(locator: TraceLocator): Promise<TraceLoad>;
}

/** The narrow shape `RoundsHttpClient` satisfies for the locator's sandbox arm: it already
 * knows how to build a `TraceReader` over its own runs (`createTraceReader()`). Declared here
 * rather than imported from `rounds-http.ts` so this module never depends on the concrete HTTP
 * client — a test double only needs to shed this one method. */
export interface SandboxTraceClient {
  createTraceReader(): TraceReader;
}

/** The narrow shape `TournamentReadClient` satisfies for the locator's graded arm: opens a
 * member's graded-round trace addressed by `(round, colony)` under the tournament-scoped
 * credential — never by run id, because the family it belongs to has no route that accepts
 * one. */
export interface GradedTraceClient {
  openTrace(round: number, colony: number): Promise<TraceLoad>;
}

/**
 * The root's **one** dispatching `TraceReader` (**D3**): routes a `sandboxRun` locator to the
 * caller's own sandbox client and a `gradedRound` locator to the tournament-read client, so
 * the two rounds screens share one seam while neither client ever learns the other's shape —
 * this factory is the only thing that is allowed to know both. The composition root
 * (`RoundsController.tsx`) builds one instance from its two already-constructed clients and
 * hands it to both screens; nothing about the sandbox screen's own open path changes, because
 * its locator arm addresses the exact same route it always has.
 */
export function createDispatchingTraceReader(
  sandbox: SandboxTraceClient,
  graded: GradedTraceClient,
): TraceReader {
  const sandboxReader = sandbox.createTraceReader();
  return {
    open(locator: TraceLocator): Promise<TraceLoad> {
      // The locator's own shape decides the route. `sandboxReader` already understands
      // `tournamentPreview` too (`RoundsHttpClient.createTraceReader()`), since the preview
      // is read over the same tournament-scoped bearer a sandbox reader already holds — a
      // `gradedRound` locator is the one shape it cannot answer.
      return locator.kind === 'gradedRound'
        ? graded.openTrace(locator.round, locator.colony)
        : sandboxReader.open(locator);
    },
  };
}

// There is deliberately **no** `INERT_TRACE_READER` constant here, unlike the
// `INERT_LOD`/`INERT_CONTROLLER`/`INERT_REPLAY_CONTROL_TRANSPORT` precedents. Those seams
// are fire-and-forget, so an inert one can honestly do nothing; `open` must *return*
// something, and every value it could return is a lie — a `corrupt` load would report a
// trace failure where there is no trace, and a `loaded` one would invent data. The
// screen's inert path is therefore the ABSENCE of a reader: with none injected it opens
// nothing at all and renders its empty state (`client-app` "A bare mount of the fifth
// route without a loaded run renders inertly").

export interface ReviewTraceOptions {
  /**
   * Sim tick rate for the mm:ss clock. **Required** (tick-rate-single-source **E4**): this
   * adapter is a trace-required surface, so its rate comes from the trace-manifest response
   * envelope's `tickRateHz` and carries no build default — a caller with no rate to pass here
   * (the device-side practice path is the one place that legitimately has none) sources it from
   * `SIM_TICK_RATE_HZ` itself rather than leaning on a fallback this signature no longer offers.
   */
  tickRateHz: number;
  /**
   * The run's per-cell store capacity, from the same envelope (WAG-100). Optional for the reason
   * {@link ReviewTrace.perCellCapacity} gives: absence renders a stored figure with no ceiling
   * beside it, which is a true statement, so there is nothing here to default wrongly.
   */
  perCellCapacity?: number;
  /**
   * Detail-range residency. Defaults to "everything resident" — a trace opened from a
   * whole downloaded file genuinely is. A range-fetching production reader (or a test
   * probing the in-flight state) supplies its own predicate; `false` renders coarse,
   * never a spinner.
   */
  isDetailResident?: (tick: number) => boolean;
  /** Called when the screen asks for a range to be made resident. */
  onRequestDetail?: (tick: number) => void;
  /** Evicted spans to expose, normalized against the decoded trace's own span. Defaults
   * to none — a trace decoded from whole, checksum-verified local bytes has no retention
   * marker to report one from. */
  evictedSpans?: readonly EvictedSpan[];
}

/**
 * Normalizes evicted spans against a trace's own tick span: ascending by `fromTick`,
 * overlapping spans merged, and every span clamped to `[firstTick, terminalTick]`
 * (`client-rounds-review`, the "retention signal reaches the trace model" requirement).
 *
 * Both consumers — the tier indicator and the scrub track — read this **one** normalized
 * signal rather than deriving their own, so a malformed, unordered, overlapping or
 * out-of-span span reported by the server can never produce a marking wider than the
 * track. A span that clamps to an empty range (wholly outside the trace's own span) is
 * dropped rather than rendered as a zero-width mark at the boundary.
 */
export function normalizeEvictedSpans(
  spans: readonly EvictedSpan[],
  firstTick: number,
  terminalTick: number,
): EvictedSpan[] {
  const clamped = spans
    .map((span): EvictedSpan => {
      // An inverted individual span (`fromTick > toTick`) is normalized by its own
      // min/max before the trace-wide clamp, rather than trusted as ordered.
      const lo = Math.min(span.fromTick, span.toTick);
      const hi = Math.max(span.fromTick, span.toTick);
      return {
        fromTick: Math.max(firstTick, lo),
        toTick: Math.min(terminalTick, hi),
      };
    })
    .filter((span) => span.fromTick <= span.toTick)
    .sort((a, b) => a.fromTick - b.fromTick);

  const merged: EvictedSpan[] = [];
  for (const span of clamped) {
    const last = merged[merged.length - 1];
    if (last !== undefined && span.fromTick <= last.toTick) {
      last.toTick = Math.max(last.toTick, span.toTick);
    } else {
      merged.push({ ...span });
    }
  }
  return merged;
}

/**
 * Adapts a decoded trace into the screen's `ReviewTrace` seam. The residency predicate
 * is the whole reason this adapter exists: the decoder knows only what bytes say, while
 * the screen needs to know what is *available right now*.
 *
 * `options` has no default (tick-rate-single-source **E4**): `tickRateHz` is a required field
 * of {@link ReviewTraceOptions}, so a caller with no rate to pass fails to compile rather than
 * silently receiving 15 — the enforcement mechanism, not an oversight.
 */
export function reviewTraceFromDecoded(
  decoded: DecodedTrace,
  options: ReviewTraceOptions,
): ReviewTrace {
  const isResident = options.isDetailResident ?? (() => true);
  const evictedSpans = normalizeEvictedSpans(
    options.evictedSpans ?? [],
    decoded.firstTick,
    decoded.terminalTick,
  );
  return {
    firstTick: decoded.firstTick,
    terminalTick: decoded.terminalTick,
    tickRateHz: options.tickRateHz,
    fieldSize: decoded.manifest.denominators.fieldSize,
    perCellCapacity: options.perCellCapacity,
    coarseAt: (tick) => decoded.coarseAt(tick),
    detailAt: (tick) => (isResident(tick) ? decoded.detailAt(tick) : null),
    stateNames: decoded.stateNames,
    requestDetail: (tick) => options.onRequestDetail?.(tick),
    evictedSpans,
  };
}

/**
 * What an in-flight trace needs told to it, because a staged generation has no manifest to read it
 * off.
 *
 * No field has a default, and none could have an honest one — which is the point of them being
 * required rather than optional. `reviewTraceFromDecoded` gets `fieldSize`/`tickRateHz` from
 * `decoded.manifest`/its own envelope-sourced option; there is no manifest here (that is the
 * whole definition of "still being written"), so the caller that configured the run is the only
 * thing that knows them.
 */
export interface InFlightTraceOptions {
  /**
   * The match's field edge length — the quantization denominator positions are dequantized against.
   * The `rounds` balance preset **derives** it from the roster, so the client must be told it by the
   * thing that configured the run rather than recomputing it and risking a different answer.
   */
  fieldSize: number;
  /** The run's configured horizon: what the track depicts and the clock counts against. */
  expectedTerminalTick: number;
  /**
   * Sim tick rate for the mm:ss clock. **Required**, no build default (tick-rate-single-source
   * **E4**), for the same reason `fieldSize`/`expectedTerminalTick` already are: a staged
   * generation has no manifest to read it off, so the caller that configured the run is the only
   * thing that knows it.
   */
  tickRateHz: number;
  /**
   * The run's per-cell store capacity (WAG-100). Optional on the terms
   * {@link ReviewTrace.perCellCapacity} sets out: a watcher that does not know it renders a
   * stored figure with no ceiling, which claims nothing false.
   */
  perCellCapacity?: number;
}

/**
 * Adapts a staged generation's readable prefix into the **same** `ReviewTrace` seam the world view
 * already renders — so watching a run before it finishes is the shipped review surface doing its
 * ordinary job, not a second renderer that could disagree with it.
 *
 * This is the other half of `rounds-local-trace-store`'s carve-out, and the half that turns the
 * permission into an experience: the extent's *width* was already observable as a byte count, which
 * is what let the playhead be dragged into it, and this is what puts something at the position the
 * playhead reaches.
 *
 * Four shapes differ from {@link reviewTraceFromDecoded}, each for its own reason:
 *
 *   * **`terminalTick` is the configured horizon, not the write head.** The track depicts the whole
 *     run — that is what makes the written extent *distinguishable* from the remainder — so a span
 *     that grew tick by tick would make the bar's meaning change under the player. It is widened to
 *     contain the write head if a run overruns its expectation, because a playhead outside the span
 *     it is scrubbing is not depictable at all.
 *   * **`firstTick` is `0`, and deliberately not `prefix.firstTick`** — the same rule as the row
 *     above, applied to the other end. The span this trace reports is the run's *configured* span;
 *     what has actually been written is a separate signal (`written-extent.ts`), and mixing the two
 *     would leave the bar's start tracking the extent while its end tracked the horizon. A run's
 *     configured span begins at tick 0 by construction, so the two coincide today; if a run ever
 *     began later, `0` would still be the honest start of what the track depicts. Stated here because
 *     its absence from this list once read as an oversight (and because
 *     `practice-watch.test.tsx`'s handover test asserts the published trace agrees, so a divergence
 *     fails as itself rather than as a mysterious camera reframe).
 *   * **`detailAt` is `null` past the write head**, which `detail-continuity.ts` already reads as
 *     "hold the last resident frame". A playhead that outruns the writer therefore holds the last
 *     written moment rather than blanking — the same behaviour a not-yet-resident HTTP range gets.
 *   * **No `onCorrupt`, and no evicted spans.** A prefix that will not decode is simply a shorter
 *     prefix ({@link decodeInFlightPrefix} truncates rather than throwing), so there is no post-open
 *     corruption this trace could ever report; and nothing can have been evicted from a trace that
 *     has not been published.
 *
 * `requestDetail` is a no-op on purpose rather than being omitted: every byte this trace will ever
 * hold is being written locally, so there is no one to ask, and the seam stays the shape the screen
 * already calls without a special case.
 */
export function reviewTraceFromInFlight(
  prefix: InFlightPrefix,
  options: InFlightTraceOptions,
): ReviewTrace {
  const writeHead = prefix.writeHeadTick ?? 0;
  return {
    firstTick: 0,
    terminalTick: Math.max(options.expectedTerminalTick, writeHead),
    tickRateHz: options.tickRateHz,
    fieldSize: options.fieldSize,
    perCellCapacity: options.perCellCapacity,
    coarseAt: (tick) => prefix.coarseAt(tick),
    detailAt: (tick) => prefix.detailAt(tick),
    stateNames: prefix.stateNames,
    // **A getter over the prefix, never a captured number.** The watcher grows one trace in place and
    // updates `prefix.writeHeadTick` on every fold, so a value read once here would be stale from the
    // next delta onward — and it is precisely the bound the playhead and the seek rule are clamped to,
    // so a stale one would re-open the empty-world failure it exists to close.
    get writeHeadTick(): number | null {
      return prefix.writeHeadTick;
    },
    requestDetail: () => {},
    evictedSpans: [],
  };
}
